import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { isMobileByTouch } from '../../utils/helpers';

type TFocusTargetGetter = () => HTMLElement | null;

/** Input types typed into with the on-screen keyboard; taps on other inputs (checkbox, switch, file, …) keep the native behaviour. */
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'tel', 'url', 'password', 'number', 'email']);

const isTextField = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement =>
    target instanceof HTMLTextAreaElement || (target instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(target.type));

/** Focuses like a tap would, but without scrolling the element into view (it would be taken by the sheet's onScroll for a swipe). */
const focusWithoutScroll = (element: HTMLElement) => {
    element.focus({ preventScroll: true });
    // A programmatic focus may leave the caret at the start of the value. `selectionStart` is null for types
    // without selection (number, email), where setSelectionRange throws.
    if (isTextField(element) && element.selectionStart !== null) {
        const end = element.value.length;
        element.setSelectionRange(end, end);
    }
};

/** What a widget rendered in the bottom sheet of SeparateContentLayout can do with the sheet. */
export type TSeparateLayoutSheet = {
    /** Expands the sheet. Call it when an input inside the sheet gets focus. */
    open: () => void;
    /** Sets the element focused when the user expands the sheet. Returns the unregister function. */
    registerFocusTarget: (getTarget: TFocusTargetGetter) => () => void;
};

export const SeparateLayoutSheetContext = createContext<TSeparateLayoutSheet | null>(null);

/** The sheet the widget is rendered in, or `null` outside SeparateContentLayout. */
export const useSeparateLayoutSheet = () => useContext(SeparateLayoutSheetContext);

/**
 * Syncs the focus of the sheet's input with the sheet on touch devices: focus expands the sheet,
 * the user expanding the sheet focuses the input (the keyboard appears), collapsing it blurs the input (the keyboard hides).
 * @param containerRef the sheet container; only focus inside it is blurred on collapse.
 * @param onOpen expands the sheet when an input inside it gets focus; returns `true` if it started expanding.
 */
export const useSheetFocusSync = (containerRef: React.RefObject<HTMLElement | null>, onOpen: () => boolean) => {
    const getTargetRef = useRef<TFocusTargetGetter | null>(null);
    const pendingFocusRef = useRef(false);
    const isTouchingRef = useRef(false);
    const onOpenRef = useRef(onOpen);

    useLayoutEffect(() => {
        onOpenRef.current = onOpen;
    });

    const sheet = useMemo<TSeparateLayoutSheet>(
        () => ({
            open: () => {
                if (isMobileByTouch()) {
                    onOpenRef.current();
                }
            },
            registerFocusTarget: (getTarget) => {
                getTargetRef.current = getTarget;
                return () => {
                    if (getTargetRef.current === getTarget) {
                        getTargetRef.current = null;
                    }
                };
            },
        }),
        [],
    );

    /** Call synchronously inside a touch/click handler: iOS shows the keyboard only for `focus()` made in a user gesture. */
    const focusTarget = useCallback(() => {
        pendingFocusRef.current = false;
        if (isMobileByTouch()) {
            const target = getTargetRef.current?.();
            if (target) {
                focusWithoutScroll(target);
            }
        }
    }, []);

    /**
     * For an expand that is not a user gesture (scroll): focuses the target when the finger of the current touch is lifted.
     * Without a touch in progress (a scroll delivered after the finger was lifted, a mouse wheel / trackpad) it does nothing,
     * so a later unrelated tap does not steal the focus.
     */
    const focusTargetOnTouchEnd = useCallback(() => {
        pendingFocusRef.current = isTouchingRef.current && isMobileByTouch();
    }, []);

    const onTouchStart = useCallback(() => {
        isTouchingRef.current = true;
    }, []);

    const onTouchCancel = useCallback(() => {
        isTouchingRef.current = false;
        pendingFocusRef.current = false;
    }, []);

    const onTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            isTouchingRef.current = false;
            if (pendingFocusRef.current) {
                focusTarget();
                return;
            }
            const target = e.target;
            if (
                isMobileByTouch() &&
                isTextField(target) &&
                document.activeElement !== target &&
                containerRef.current?.contains(target) &&
                onOpenRef.current()
            ) {
                // A tap on a field of the collapsed sheet: the native focus would scroll the field into view
                // while the sheet expands and lift its content too high. Focus it like an expand by the user instead.
                e.preventDefault();
                focusWithoutScroll(target);
            }
        },
        [containerRef, focusTarget],
    );

    const blurFocused = useCallback(() => {
        pendingFocusRef.current = false;
        const active = document.activeElement;
        if (active instanceof HTMLElement && containerRef.current?.contains(active)) {
            active.blur();
        }
    }, [containerRef]);

    return { sheet, focusTarget, focusTargetOnTouchEnd, onTouchStart, onTouchEnd, onTouchCancel, blurFocused };
};
